import type { inferRouterOutputs } from '@trpc/server';
import type { BarDuration } from '@einhaus/project314-types';
import type { AppRouter } from '@/types/appRouter';

type RouterOutputs = inferRouterOutputs<AppRouter>;
export type LeaderboardOutput = RouterOutputs['strategyVariantComparison']['listLeaderboard'];
export type LeaderboardEntry = LeaderboardOutput['entries'][number];
export type LeaderboardRankBy = LeaderboardOutput['rankBy'];
export type LeaderboardInstrument = LeaderboardEntry['instruments'][number];

/** Display order and names for what a strategy trades; filters and URLs keep this order. */
export const LEADERBOARD_INSTRUMENTS: { value: LeaderboardInstrument; label: string }[] = [
    { value: 'stocks', label: 'Stocks & ETFs' },
    { value: 'crypto', label: 'Crypto' },
    { value: 'stockOptions', label: 'Stock options' },
    { value: 'indexOptions', label: 'Index options' },
    { value: 'futures', label: 'Futures' }
];

const INSTRUMENT_LABELS = new Map(LEADERBOARD_INSTRUMENTS.map((instrument) => [instrument.value, instrument.label]));

export const instrumentLabel = (instrument: LeaderboardInstrument): string => INSTRUMENT_LABELS.get(instrument) ?? instrument;

export const LEADERBOARD_STATUS_FILTERS = ['all', 'trading', 'paused'] as const;
export type LeaderboardStatusFilter = (typeof LEADERBOARD_STATUS_FILTERS)[number];

export const LEADERBOARD_ROLE_FILTERS = ['all', 'base', 'variation', 'standalone'] as const;
export type LeaderboardRoleFilter = (typeof LEADERBOARD_ROLE_FILTERS)[number];

export const LEADERBOARD_BIAS_FILTERS = ['all', 'bullish', 'bearish', 'mixed', 'unknown'] as const;
export type LeaderboardBiasFilter = (typeof LEADERBOARD_BIAS_FILTERS)[number];

/** `switchedOn` means at least one portfolio's subscription switch is on; the strategy may still be paused globally. */
export const LEADERBOARD_SUBSCRIPTION_FILTERS = ['all', 'subscribed', 'switchedOn', 'notSubscribed'] as const;
export type LeaderboardSubscriptionFilter = (typeof LEADERBOARD_SUBSCRIPTION_FILTERS)[number];

export const LEADERBOARD_RANK_BY_OPTIONS: { value: LeaderboardRankBy; label: string }[] = [
    { value: 'returnOnRiskCapital', label: 'Return on risk capital' },
    { value: 'return', label: 'Avg return per trade' },
    { value: 'winRate', label: 'Win rate' },
    { value: 'profitFactor', label: 'Profit factor' }
];

interface ThresholdDefinition<TKey extends string = string> {
    key: TKey;
    label: string;
    suffix?: string;
    bound: 'min' | 'max';
    integer?: boolean;
    select: (entry: LeaderboardEntry) => number | null;
}

// Each threshold reads one value off a row. A row passes only when that value is known and on the right side of
// the limit, so a strategy without results never slips through an active threshold.
const THRESHOLD_DEFINITIONS = [
    { key: 'minTrades', label: 'Min trades', bound: 'min', integer: true, select: (entry: LeaderboardEntry) => entry.metrics.totalTrades },
    { key: 'minWinRate', label: 'Min win rate', suffix: '%', bound: 'min', select: (entry: LeaderboardEntry) => entry.metrics.avgWinRate },
    {
        key: 'minAvgReturn',
        label: 'Min avg trade',
        suffix: '%',
        bound: 'min',
        select: (entry: LeaderboardEntry) => entry.metrics.avgReturnPercent
    },
    {
        key: 'minRoc',
        label: 'Min return on risk',
        suffix: '%',
        bound: 'min',
        select: (entry: LeaderboardEntry) => entry.metrics.avgReturnOnRiskCapitalPercent
    },
    {
        key: 'minProfitFactor',
        label: 'Min profit factor',
        bound: 'min',
        select: (entry: LeaderboardEntry) => entry.metrics.profitFactor
    },
    {
        // Drawdown is stored as a negative percentage; the limit is entered as a positive "no worse than" value.
        key: 'maxDrawdown',
        label: 'Max drawdown',
        suffix: '%',
        bound: 'max',
        select: (entry: LeaderboardEntry) =>
            entry.metrics.avgMaxDrawdownPercent === null ? null : Math.abs(entry.metrics.avgMaxDrawdownPercent)
    },
    {
        key: 'minEntities',
        label: 'Min entities',
        bound: 'min',
        integer: true,
        select: (entry: LeaderboardEntry) => entry.bestCombination?.entityCount ?? null
    },
    {
        key: 'minWalkForwardTrades',
        label: 'Min WF trades',
        bound: 'min',
        integer: true,
        select: (entry: LeaderboardEntry) => entry.walkForward.metrics?.totalTrades ?? null
    },
    {
        key: 'minWalkForwardWinRate',
        label: 'Min WF win rate',
        suffix: '%',
        bound: 'min',
        select: (entry: LeaderboardEntry) => entry.walkForward.metrics?.avgWinRate ?? null
    },
    {
        key: 'minWalkForwardAvgReturn',
        label: 'Min WF avg trade',
        suffix: '%',
        bound: 'min',
        select: (entry: LeaderboardEntry) => entry.walkForward.metrics?.avgReturnPercent ?? null
    }
] as const satisfies readonly ThresholdDefinition[];

export type LeaderboardThresholdKey = (typeof THRESHOLD_DEFINITIONS)[number]['key'];
export type LeaderboardThreshold = ThresholdDefinition<LeaderboardThresholdKey>;

export const LEADERBOARD_THRESHOLDS: readonly LeaderboardThreshold[] = THRESHOLD_DEFINITIONS;

/** Only active thresholds are present; clearing a threshold removes its key. */
export type LeaderboardThresholds = Partial<Record<LeaderboardThresholdKey, number>>;

export interface LeaderboardFilters {
    search: string;
    familyIds: number[];
    status: LeaderboardStatusFilter;
    role: LeaderboardRoleFilter;
    bias: LeaderboardBiasFilter;
    subscription: LeaderboardSubscriptionFilter;
    /** Keeps strategies that trade any of these; empty means every instrument. */
    instruments: LeaderboardInstrument[];
    duration: BarDuration | null;
    resultsOnly: boolean;
    rankBy: LeaderboardRankBy;
    thresholds: LeaderboardThresholds;
}

export const DEFAULT_LEADERBOARD_FILTERS: LeaderboardFilters = {
    search: '',
    familyIds: [],
    status: 'all',
    role: 'all',
    bias: 'all',
    subscription: 'all',
    instruments: [],
    duration: null,
    resultsOnly: false,
    rankBy: 'returnOnRiskCapital',
    thresholds: {}
};

/** Returns a copy with one threshold set, or removed when `value` is null. */
export function withThreshold(
    thresholds: LeaderboardThresholds,
    key: LeaderboardThresholdKey,
    value: number | null
): LeaderboardThresholds {
    const next = { ...thresholds };

    if (value === null) {
        delete next[key];
    } else {
        next[key] = value;
    }

    return next;
}

/** The metric a "best configuration by" choice ranks with, used for the initial row order. */
export function rankValue(entry: LeaderboardEntry, rankBy: LeaderboardRankBy): number | null {
    switch (rankBy) {
        case 'return':
            return entry.metrics.avgReturnPercent;
        case 'returnOnRiskCapital':
            return entry.metrics.avgReturnOnRiskCapitalPercent;
        case 'winRate':
            return entry.metrics.avgWinRate;
        case 'profitFactor':
            return entry.metrics.profitFactor;
    }
}

/** Highest ranked first; strategies without a ranked result go last, newest first. */
export function sortByRank(entries: LeaderboardEntry[], rankBy: LeaderboardRankBy): LeaderboardEntry[] {
    return [...entries].sort((left, right) => {
        const leftValue = rankValue(left, rankBy);
        const rightValue = rankValue(right, rankBy);

        if (leftValue === null || rightValue === null) {
            if (leftValue !== rightValue) return leftValue === null ? 1 : -1;
            return right.strategyId - left.strategyId;
        }

        return rightValue - leftValue || right.strategyId - left.strategyId;
    });
}

const searchHaystack = (entry: LeaderboardEntry): string =>
    [
        entry.strategyId,
        entry.name,
        entry.description,
        entry.variantLabel ?? '',
        entry.familyName ?? '',
        entry.bestCombination?.label ?? '',
        ...entry.instruments.map(instrumentLabel),
        ...entry.portfolioSubscriptions.map((subscription) => subscription.portfolioName)
    ]
        .join(' ')
        .toLowerCase();

const matchesSubscriptionFilter = (entry: LeaderboardEntry, filter: LeaderboardSubscriptionFilter): boolean => {
    switch (filter) {
        case 'all':
            return true;
        case 'subscribed':
            return entry.portfolioSubscriptions.length > 0;
        case 'switchedOn':
            return entry.portfolioSubscriptions.some((subscription) => subscription.subscriptionActive);
        case 'notSubscribed':
            return entry.portfolioSubscriptions.length === 0;
    }
};

/** Applies every active filter in one pass over the rows; the result keeps the input order. */
export function filterLeaderboardEntries(entries: LeaderboardEntry[], filters: LeaderboardFilters): LeaderboardEntry[] {
    const searchTokens = filters.search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const familyIds = new Set(filters.familyIds);
    const instruments = new Set(filters.instruments);
    const activeThresholds = LEADERBOARD_THRESHOLDS.flatMap((threshold) => {
        const limit = filters.thresholds[threshold.key];
        return limit === undefined ? [] : [{ threshold, limit }];
    });

    return entries.filter((entry) => {
        if (filters.status === 'trading' && !entry.active) return false;
        if (filters.status === 'paused' && entry.active) return false;
        if (filters.role !== 'all' && entry.familyRole !== filters.role) return false;
        if (filters.bias !== 'all' && entry.underlyingBias !== filters.bias) return false;
        if (!matchesSubscriptionFilter(entry, filters.subscription)) return false;
        if (instruments.size > 0 && !entry.instruments.some((instrument) => instruments.has(instrument))) return false;
        if (filters.duration !== null && entry.duration !== filters.duration) return false;
        if (familyIds.size > 0 && (entry.familyId === null || !familyIds.has(entry.familyId))) return false;
        if (filters.resultsOnly && entry.bestCombination === null) return false;

        for (const { threshold, limit } of activeThresholds) {
            const value = threshold.select(entry);

            if (value === null) return false;
            if (threshold.bound === 'min' ? value < limit : value > limit) return false;
        }

        if (searchTokens.length > 0) {
            const haystack = searchHaystack(entry);
            if (!searchTokens.every((token) => haystack.includes(token))) return false;
        }

        return true;
    });
}

export const countActiveFilters = (filters: LeaderboardFilters): number =>
    Number(filters.search.trim().length > 0) +
    Number(filters.familyIds.length > 0) +
    Number(filters.status !== 'all') +
    Number(filters.role !== 'all') +
    Number(filters.bias !== 'all') +
    Number(filters.subscription !== 'all') +
    Number(filters.instruments.length > 0) +
    Number(filters.duration !== null) +
    Number(filters.resultsOnly) +
    Object.keys(filters.thresholds).length;

const parseNumber = (raw: string | null): number | null => {
    if (raw === null || raw.trim() === '') return null;

    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
};

/**
 * Reads filters from the page URL so a filtered view can be bookmarked or shared. Unknown or malformed values fall
 * back to their defaults instead of breaking the page, because these URLs are edited by hand.
 */
export function parseLeaderboardFilters(params: URLSearchParams, durations: readonly BarDuration[]): LeaderboardFilters {
    let thresholds: LeaderboardThresholds = {};

    for (const threshold of LEADERBOARD_THRESHOLDS) {
        thresholds = withThreshold(thresholds, threshold.key, parseNumber(params.get(threshold.key)));
    }

    return {
        search: params.get('q') ?? '',
        familyIds: (params.get('families') ?? '')
            .split(',')
            .map((value) => Number(value))
            .filter((value) => Number.isInteger(value) && value > 0),
        status: LEADERBOARD_STATUS_FILTERS.find((value) => value === params.get('status')) ?? 'all',
        role: LEADERBOARD_ROLE_FILTERS.find((value) => value === params.get('role')) ?? 'all',
        bias: LEADERBOARD_BIAS_FILTERS.find((value) => value === params.get('bias')) ?? 'all',
        subscription: LEADERBOARD_SUBSCRIPTION_FILTERS.find((value) => value === params.get('portfolios')) ?? 'all',
        instruments: LEADERBOARD_INSTRUMENTS.map((instrument) => instrument.value).filter((instrument) =>
            (params.get('instruments') ?? '').split(',').includes(instrument)
        ),
        duration: durations.find((value) => value === params.get('duration')) ?? null,
        resultsOnly: params.get('results') === '1',
        rankBy:
            LEADERBOARD_RANK_BY_OPTIONS.find((option) => option.value === params.get('rankBy'))?.value ??
            DEFAULT_LEADERBOARD_FILTERS.rankBy,
        thresholds
    };
}

/** Writes only non-default values, so the default view keeps a clean URL. */
export function serializeLeaderboardFilters(filters: LeaderboardFilters): URLSearchParams {
    const params = new URLSearchParams();

    if (filters.search.trim()) params.set('q', filters.search);
    if (filters.familyIds.length > 0) params.set('families', filters.familyIds.join(','));
    if (filters.status !== 'all') params.set('status', filters.status);
    if (filters.role !== 'all') params.set('role', filters.role);
    if (filters.bias !== 'all') params.set('bias', filters.bias);
    if (filters.subscription !== 'all') params.set('portfolios', filters.subscription);
    if (filters.instruments.length > 0) params.set('instruments', filters.instruments.join(','));
    if (filters.duration !== null) params.set('duration', filters.duration);
    if (filters.resultsOnly) params.set('results', '1');
    if (filters.rankBy !== DEFAULT_LEADERBOARD_FILTERS.rankBy) params.set('rankBy', filters.rankBy);

    for (const threshold of LEADERBOARD_THRESHOLDS) {
        const value = filters.thresholds[threshold.key];
        if (value !== undefined) params.set(threshold.key, String(value));
    }

    return params;
}
